// Iter72 — Payload Builder v1.
//
// Pure-frontend helper that lets the operator construct a
// scanner-ready JSON payload for one of the five module scanners
// (BPCS, BCCS, Iron Condor, CFM-CC-SHARES, CFM-CC-LEAPS). The
// generated JSON is shaped exactly like the request body each
// scanner endpoint expects:
//   { watchlist, payloads_by_symbol, max_symbols?, config? }
//
// The user can Copy the JSON to their clipboard or Download it as a
// .json artifact, then PASTE it manually into the relevant scanner
// panel. This tile NEVER auto-submits to any scanner endpoint, nor
// fetches any live market data.
//
// HARD CONSTRAINTS (asserted by Iter72 tests):
//   * Zero outbound HTTP traffic. No axios import, no fetch call,
//     no XHR object, no beacon usage.
//   * No backend / scanner / preview / candidates / journal /
//     broker / account / position endpoint reference.
//   * No trade execution / assignment automation / exercise
//     automation keywords.
//   * No effect-hook side-effects on mount.
//
// Static testid registry (asserted by Iter72 tests):
//   payload-builder-module-BPCS
//   payload-builder-module-BCCS
//   payload-builder-module-IRON_CONDOR
//   payload-builder-module-CFM_CC_SHARES
//   payload-builder-module-CFM_CC_LEAPS
//   payload-builder-warning-0  (and indexed siblings)

import React, { useMemo, useState } from "react";
import {
  AlertTriangle, ChevronDown, ChevronUp, ShieldAlert,
  Cog, Copy, Download, Eraser, ListChecks, FileJson, Check,
} from "lucide-react";

// ── Module catalog ────────────────────────────────────────────────────

const MODULES = [
  {
    id: "BPCS",         label: "BPCS — Bull Put Credit Spread",
    accent: "#007AFF",  endpointHint: "/forge/toolbox/scanner/bpcs-preview",
    shape: "spread_put",
    chainSeed: [
      { type: "put", strike: 490.0, bid: 1.60, ask: 1.65,
        delta: -0.25, open_interest: 1500, volume: 100,
        expiration: "2026-06-21", dte: 21 },
      { type: "put", strike: 485.0, bid: 0.55, ask: 0.60,
        delta: -0.12, open_interest: 1500, volume: 80,
        expiration: "2026-06-21", dte: 21 },
    ],
  },
  {
    id: "BCCS",         label: "BCCS — Bear Call Credit Spread",
    accent: "#FF3366",  endpointHint: "/forge/toolbox/scanner/bccs-preview",
    shape: "spread_call",
    chainSeed: [
      { type: "call", strike: 505.0, bid: 4.10, ask: 4.15,
        delta: 0.50, open_interest: 5000, volume: 200 },
      { type: "call", strike: 510.0, bid: 1.50, ask: 1.55,
        delta: 0.22, open_interest: 5000, volume: 200 },
      { type: "call", strike: 515.0, bid: 0.42, ask: 0.45,
        delta: 0.10, open_interest: 4000, volume: 150 },
    ],
  },
  {
    id: "IRON_CONDOR",  label: "Iron Condor",
    accent: "#FFB800",  endpointHint: "/forge/toolbox/scanner/iron-condor-preview",
    shape: "iron_condor",
    chainSeed: [
      { type: "put",  strike: 493.0, bid: 1.60, ask: 1.65,
        delta: -0.18, open_interest: 2000, volume: 100 },
      { type: "put",  strike: 488.0, bid: 0.50, ask: 0.55,
        delta: -0.08, open_interest: 1500, volume: 80 },
      { type: "call", strike: 507.0, bid: 1.70, ask: 1.75,
        delta: 0.18, open_interest: 2000, volume: 100 },
      { type: "call", strike: 512.0, bid: 0.55, ask: 0.60,
        delta: 0.08, open_interest: 1500, volume: 80 },
    ],
  },
  {
    id: "CFM_CC_SHARES", label: "CFM-CC-SHARES",
    accent: "#22D3CC",   endpointHint: "/forge/toolbox/scanner/cfm-shares-preview",
    shape: "cfm_shares",
    chainSeed: [
      { type: "call", strike: 498.0, bid: 5.80, ask: 5.85,
        delta: 0.60, open_interest: 2000, volume: 120,
        expiration: "2026-06-21", dte: 14 },
      { type: "call", strike: 500.0, bid: 4.50, ask: 4.55,
        delta: 0.50, open_interest: 2500, volume: 150,
        expiration: "2026-06-21", dte: 14 },
      { type: "call", strike: 503.0, bid: 2.80, ask: 2.85,
        delta: 0.40, open_interest: 2200, volume: 130,
        expiration: "2026-06-21", dte: 14 },
    ],
  },
  {
    id: "CFM_CC_LEAPS", label: "CFM-CC-LEAPS / PMCC",
    accent: "#A855F7",  endpointHint: "/forge/toolbox/scanner/cfm-leaps-preview",
    shape: "cfm_leaps",
    leapsSeed: [{
      type: "call", strike: 400.0, bid: 110.0, ask: 112.0,
      delta: 0.85, theta: -0.05, open_interest: 600, volume: 25,
      expiration: "2027-01-15", dte: 365,
    }],
    shortCallsSeed: [{
      type: "call", strike: 500.0, bid: 3.20, ask: 3.30,
      delta: 0.50, theta: -0.14, open_interest: 2500, volume: 150,
      expiration: "2026-06-21", dte: 14,
    }],
  },
];

const DEFAULT_WES = {
  symbols: "SPY",
  spot_price: "500",
  val: "495",
  vah: "510",
  poc: "502",
  wes_p: "8",
  wes_c: "6",
  delta_VAL: "0.3",
  delta_VAH: "",
  closed_bar_confirmed: true,
  ivr: "35",
  regime_hint: "",
};

const DEFAULT_SHARES_CTX = {
  shares_owned: "100",
  share_basis: "498.0",
  total_call_credits: "2.50",
  minimum_exit_price: "500.0",
  allow_buy_write: true,
  assignment_policy: "accept_if_min_exit_met",
};

const DEFAULT_LEAPS_CTX = {
  allow_leaps: true,
  total_short_call_credits: "0.0",
  assignment_policy: "manage_short_call",
  existing_leaps_position: "",   // raw JSON or blank
};

// ── Helpers ────────────────────────────────────────────────────────────

function parseNum(s) {
  if (s === "" || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function safeJsonParse(s, fallback) {
  if (!s || !s.trim()) return fallback;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function parseSymbols(s) {
  return (s || "")
    .split(/[\s,]+/)
    .map(x => x.trim().toUpperCase())
    .filter(x => x.length > 0);
}

function pickWes(wes) {
  const obj = {
    val: parseNum(wes.val),
    vah: parseNum(wes.vah),
    poc: parseNum(wes.poc),
    wes_p: parseNum(wes.wes_p),
    wes_c: parseNum(wes.wes_c),
    closed_bar_confirmed: !!wes.closed_bar_confirmed,
  };
  const dvl = parseNum(wes.delta_VAL);
  if (dvl !== null) obj.delta_VAL = dvl;
  const dvh = parseNum(wes.delta_VAH);
  if (dvh !== null) obj.delta_VAH = dvh;
  return obj;
}

function buildPerSymbolPayload(mod, wes, chainText, leapsText, shortText, sharesCtx, leapsCtx) {
  const ivr = parseNum(wes.ivr);
  const spot = parseNum(wes.spot_price);
  const base = {
    spot_price: spot,
    wes: pickWes(wes),
  };
  if (ivr !== null) base.ivr = ivr;
  if (wes.regime_hint && wes.regime_hint.trim()) {
    base.regime_hint = wes.regime_hint.trim();
  }

  if (mod.shape === "cfm_leaps") {
    base.option_chains = {
      leaps_calls: safeJsonParse(leapsText, []) || [],
      short_calls: safeJsonParse(shortText, []) || [],
    };
    base.account_context = {
      allow_leaps: !!leapsCtx.allow_leaps,
      total_short_call_credits:
        parseNum(leapsCtx.total_short_call_credits) ?? 0,
      assignment_policy: leapsCtx.assignment_policy || "manage_short_call",
    };
    const existing = safeJsonParse(leapsCtx.existing_leaps_position, null);
    if (existing && typeof existing === "object") {
      base.account_context.existing_leaps_position = existing;
    }
  } else {
    base.chain = safeJsonParse(chainText, []) || [];
    if (mod.shape === "cfm_shares") {
      base.account_context = {
        shares_owned: parseNum(sharesCtx.shares_owned) ?? 0,
        share_basis: parseNum(sharesCtx.share_basis) ?? 0,
        total_call_credits:
          parseNum(sharesCtx.total_call_credits) ?? 0,
        minimum_exit_price:
          parseNum(sharesCtx.minimum_exit_price) ?? 0,
        allow_buy_write: !!sharesCtx.allow_buy_write,
        assignment_policy: sharesCtx.assignment_policy || "accept_if_min_exit_met",
      };
    }
    if (mod.shape === "iron_condor" || mod.shape === "spread_call") {
      // Iron Condor + BCCS reference an `account_profile` for sizing
      // and a default `wheel_engine.actionable: false` flag the
      // toolbox evaluators expect so they don't shadow the
      // protected flagship.
      base.wheel_engine = { actionable: false };
      base.account_profile = {
        account_value: 100000,
        max_risk_per_trade_pct: 0.01,
      };
    }
  }
  return base;
}

function validate(mod, wes, chainText, leapsText, shortText) {
  const warnings = [];
  const symbols = parseSymbols(wes.symbols);
  if (symbols.length === 0) warnings.push("At least one symbol is required.");
  for (const f of ["spot_price", "val", "vah", "poc", "wes_p", "wes_c"]) {
    if (parseNum(wes[f]) === null) {
      warnings.push(`WES field "${f}" must be a number.`);
    }
  }
  if (mod.shape === "cfm_leaps") {
    const lp = safeJsonParse(leapsText, undefined);
    if (lp === null) warnings.push("LEAPS calls JSON is invalid.");
    else if (!Array.isArray(lp) || lp.length === 0) {
      warnings.push("LEAPS calls must be a non-empty array.");
    }
    const sc = safeJsonParse(shortText, undefined);
    if (sc === null) warnings.push("Short calls JSON is invalid.");
    else if (!Array.isArray(sc) || sc.length === 0) {
      warnings.push("Short calls must be a non-empty array.");
    }
  } else {
    const ch = safeJsonParse(chainText, undefined);
    if (ch === null) warnings.push("Chain JSON is invalid.");
    else if (!Array.isArray(ch) || ch.length === 0) {
      warnings.push("Chain must be a non-empty array.");
    }
  }
  return warnings;
}

// ── Tile ──────────────────────────────────────────────────────────────

export default function PayloadBuilderTile() {
  const [open, setOpen] = useState(false);
  const [moduleId, setModuleId] = useState("BPCS");
  const [wes, setWes] = useState(DEFAULT_WES);
  const [chainText, setChainText] = useState(() =>
    JSON.stringify(MODULES.find(m => m.id === "BPCS").chainSeed, null, 2)
  );
  const [leapsText, setLeapsText] = useState(() =>
    JSON.stringify(MODULES.find(m => m.id === "CFM_CC_LEAPS").leapsSeed, null, 2)
  );
  const [shortText, setShortText] = useState(() =>
    JSON.stringify(MODULES.find(m => m.id === "CFM_CC_LEAPS").shortCallsSeed, null, 2)
  );
  const [sharesCtx, setSharesCtx] = useState(DEFAULT_SHARES_CTX);
  const [leapsCtx, setLeapsCtx] = useState(DEFAULT_LEAPS_CTX);
  const [maxSymbols, setMaxSymbols] = useState("");
  const [generated, setGenerated] = useState(null);
  const [copyState, setCopyState] = useState("idle");
  const [downloadState, setDownloadState] = useState("idle");

  const mod = useMemo(
    () => MODULES.find(m => m.id === moduleId) || MODULES[0],
    [moduleId],
  );

  const warnings = useMemo(
    () => validate(mod, wes, chainText, leapsText, shortText),
    [mod, wes, chainText, leapsText, shortText],
  );

  function switchModule(nextId) {
    setModuleId(nextId);
    const next = MODULES.find(m => m.id === nextId) || MODULES[0];
    if (next.shape === "cfm_leaps") {
      setLeapsText(JSON.stringify(next.leapsSeed, null, 2));
      setShortText(JSON.stringify(next.shortCallsSeed, null, 2));
    } else {
      setChainText(JSON.stringify(next.chainSeed, null, 2));
    }
    setGenerated(null);
  }

  function generatePayload() {
    const symbols = parseSymbols(wes.symbols);
    const per = buildPerSymbolPayload(
      mod, wes, chainText, leapsText, shortText, sharesCtx, leapsCtx,
    );
    const payloads_by_symbol = {};
    for (const s of symbols) {
      payloads_by_symbol[s] = { ...per, symbol: s };
    }
    const out = {
      watchlist: symbols,
      payloads_by_symbol: payloads_by_symbol,
    };
    const cap = parseNum(maxSymbols);
    if (cap !== null && cap > 0) out.max_symbols = cap;
    setGenerated(out);
  }

  async function copyJson() {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(generated, null, 2),
      );
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    } finally {
      setTimeout(() => setCopyState("idle"), 2000);
    }
  }

  function downloadJson() {
    if (!generated) return;
    try {
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `arxmentis_payload_${mod.id}_${ts}.json`;
      const blob = new Blob(
        [JSON.stringify(generated, null, 2)],
        { type: "application/json" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloadState("downloaded");
    } catch {
      setDownloadState("failed");
    } finally {
      setTimeout(() => setDownloadState("idle"), 2000);
    }
  }

  function clearAll() {
    setWes(DEFAULT_WES);
    setSharesCtx(DEFAULT_SHARES_CTX);
    setLeapsCtx(DEFAULT_LEAPS_CTX);
    setMaxSymbols("");
    setGenerated(null);
    setCopyState("idle");
    setDownloadState("idle");
    if (mod.shape === "cfm_leaps") {
      setLeapsText(JSON.stringify(mod.leapsSeed, null, 2));
      setShortText(JSON.stringify(mod.shortCallsSeed, null, 2));
    } else {
      setChainText(JSON.stringify(mod.chainSeed, null, 2));
    }
  }

  return (
    <section
      data-testid="payload-builder-tile"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid="payload-builder-toggle"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Cog size={16} style={{ color: mod.accent }} />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Payload Builder · v1
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · scanner-ready JSON · no scanner execution · review before running
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Safety banner ───────────────────────────────────── */}
          <div
            data-testid="payload-builder-banner"
            className="px-4 py-2 border-b border-[#26262B] text-[10px] font-mono flex items-center gap-3 flex-wrap"
            style={{ background: `${mod.accent}0D`, color: mod.accent }}
          >
            <ShieldAlert size={12} />
            <span data-testid="payload-builder-banner-builder-only">
              Payload Builder only.
            </span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span data-testid="payload-builder-banner-no-live-fetch">
              No live data fetch.
            </span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span data-testid="payload-builder-banner-no-scanner-execution">
              No scanner execution.
            </span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span data-testid="payload-builder-banner-no-trade-execution">
              No trade execution.
            </span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span data-testid="payload-builder-banner-review-before-running">
              Review before running scanner.
            </span>
          </div>

          {/* ── Module selector ────────────────────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B] flex flex-wrap gap-2 items-center">
            <ListChecks size={11} className="text-[#5F5F66]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
              Target scanner
            </span>
            <div
              data-testid="payload-builder-module-selector"
              className="flex flex-wrap gap-1"
            >
              {MODULES.map(m => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`payload-builder-module-${m.id}`}
                  onClick={() => switchModule(m.id)}
                  className={`px-2 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-[0.12em] ${
                    moduleId === m.id
                      ? "bg-[#121216] text-[#E0E0E0]"
                      : "border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47]"
                  }`}
                  style={moduleId === m.id ? { borderColor: m.accent } : {}}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <span className="ml-auto text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]"
                   data-testid="payload-builder-endpoint-hint">
              Target: {mod.endpointHint}
            </span>
          </div>

          {/* ── Common context fields ───────────────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[10px] font-mono">
            <Field label="Symbols (comma)" testid="payload-builder-symbols"
                    value={wes.symbols}
                    onChange={(v) => setWes(p => ({ ...p, symbols: v }))} />
            <Field label="Spot" testid="payload-builder-spot"
                    value={wes.spot_price}
                    onChange={(v) => setWes(p => ({ ...p, spot_price: v }))} />
            <Field label="VAL" testid="payload-builder-val"
                    value={wes.val}
                    onChange={(v) => setWes(p => ({ ...p, val: v }))} />
            <Field label="POC" testid="payload-builder-poc"
                    value={wes.poc}
                    onChange={(v) => setWes(p => ({ ...p, poc: v }))} />
            <Field label="VAH" testid="payload-builder-vah"
                    value={wes.vah}
                    onChange={(v) => setWes(p => ({ ...p, vah: v }))} />
            <Field label="WES-P" testid="payload-builder-wes-p"
                    value={wes.wes_p}
                    onChange={(v) => setWes(p => ({ ...p, wes_p: v }))} />
            <Field label="WES-C" testid="payload-builder-wes-c"
                    value={wes.wes_c}
                    onChange={(v) => setWes(p => ({ ...p, wes_c: v }))} />
            <Field label="IVR" testid="payload-builder-ivr"
                    value={wes.ivr}
                    onChange={(v) => setWes(p => ({ ...p, ivr: v }))} />
            <Field label="delta_VAL" testid="payload-builder-delta-val"
                    value={wes.delta_VAL}
                    onChange={(v) => setWes(p => ({ ...p, delta_VAL: v }))} />
            <Field label="delta_VAH" testid="payload-builder-delta-vah"
                    value={wes.delta_VAH}
                    onChange={(v) => setWes(p => ({ ...p, delta_VAH: v }))} />
            <Field label="regime_hint (optional)"
                    testid="payload-builder-regime-hint"
                    value={wes.regime_hint}
                    onChange={(v) => setWes(p => ({ ...p, regime_hint: v }))} />
            <BoolField label="bar_closed"
                        testid="payload-builder-bar-closed"
                        value={wes.closed_bar_confirmed}
                        onChange={(v) => setWes(p => ({ ...p, closed_bar_confirmed: v }))} />
            <Field label="max_symbols (optional)"
                    testid="payload-builder-max-symbols"
                    value={maxSymbols}
                    onChange={setMaxSymbols} />
          </div>

          {/* ── Module-specific chain editors ───────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B]">
            {mod.shape !== "cfm_leaps" ? (
              <TextArea
                label="Option chain (JSON array)"
                testid="payload-builder-chain"
                value={chainText}
                onChange={setChainText}
              />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                <TextArea
                  label="LEAPS calls (JSON array)"
                  testid="payload-builder-leaps-calls"
                  value={leapsText}
                  onChange={setLeapsText}
                />
                <TextArea
                  label="Short calls (JSON array)"
                  testid="payload-builder-short-calls"
                  value={shortText}
                  onChange={setShortText}
                />
              </div>
            )}
          </div>

          {/* ── CFM-CC-SHARES account_context ───────────────────── */}
          {mod.shape === "cfm_shares" && (
            <div
              data-testid="payload-builder-shares-context"
              className="px-4 py-3 border-b border-[#26262B] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-[10px] font-mono"
            >
              <Field label="shares_owned" testid="payload-builder-shares-owned"
                      value={sharesCtx.shares_owned}
                      onChange={(v) => setSharesCtx(p => ({ ...p, shares_owned: v }))} />
              <Field label="share_basis" testid="payload-builder-share-basis"
                      value={sharesCtx.share_basis}
                      onChange={(v) => setSharesCtx(p => ({ ...p, share_basis: v }))} />
              <Field label="total_call_credits"
                      testid="payload-builder-total-call-credits"
                      value={sharesCtx.total_call_credits}
                      onChange={(v) => setSharesCtx(p => ({ ...p, total_call_credits: v }))} />
              <Field label="minimum_exit_price"
                      testid="payload-builder-minimum-exit-price"
                      value={sharesCtx.minimum_exit_price}
                      onChange={(v) => setSharesCtx(p => ({ ...p, minimum_exit_price: v }))} />
              <Field label="assignment_policy"
                      testid="payload-builder-shares-assignment-policy"
                      value={sharesCtx.assignment_policy}
                      onChange={(v) => setSharesCtx(p => ({ ...p, assignment_policy: v }))} />
              <BoolField label="allow_buy_write"
                          testid="payload-builder-allow-buy-write"
                          value={sharesCtx.allow_buy_write}
                          onChange={(v) => setSharesCtx(p => ({ ...p, allow_buy_write: v }))} />
            </div>
          )}

          {/* ── CFM-CC-LEAPS account_context ────────────────────── */}
          {mod.shape === "cfm_leaps" && (
            <div
              data-testid="payload-builder-leaps-context"
              className="px-4 py-3 border-b border-[#26262B] grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] font-mono"
            >
              <Field label="total_short_call_credits"
                      testid="payload-builder-total-short-call-credits"
                      value={leapsCtx.total_short_call_credits}
                      onChange={(v) => setLeapsCtx(p => ({ ...p, total_short_call_credits: v }))} />
              <Field label="assignment_policy"
                      testid="payload-builder-leaps-assignment-policy"
                      value={leapsCtx.assignment_policy}
                      onChange={(v) => setLeapsCtx(p => ({ ...p, assignment_policy: v }))} />
              <BoolField label="allow_leaps"
                          testid="payload-builder-allow-leaps"
                          value={leapsCtx.allow_leaps}
                          onChange={(v) => setLeapsCtx(p => ({ ...p, allow_leaps: v }))} />
              <TextArea
                label="existing_leaps_position (optional JSON)"
                testid="payload-builder-existing-leaps-position"
                value={leapsCtx.existing_leaps_position}
                onChange={(v) => setLeapsCtx(p => ({ ...p, existing_leaps_position: v }))}
                rows={4}
              />
            </div>
          )}

          {/* ── Action bar ──────────────────────────────────────── */}
          <div className="px-4 py-2 border-b border-[#26262B] flex flex-wrap items-center gap-2 text-[10px] font-mono">
            <button
              type="button"
              data-testid="payload-builder-generate"
              onClick={generatePayload}
              className="px-3 py-1 rounded-sm border uppercase tracking-[0.14em] inline-flex items-center gap-1"
              style={{
                borderColor: `${mod.accent}66`,
                background: `${mod.accent}15`,
                color: mod.accent,
              }}
            >
              <Cog size={10} /> Generate Payload
            </button>
            <button
              type="button"
              data-testid={`payload-builder-copy${copyState === "copied" ? "-success" : copyState === "failed" ? "-failed" : ""}`}
              onClick={copyJson}
              disabled={!generated}
              className={`px-3 py-1 rounded-sm border uppercase tracking-[0.12em] inline-flex items-center gap-1 ${
                copyState === "copied"
                  ? "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]"
                  : copyState === "failed"
                  ? "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]"
                  : "border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] disabled:opacity-40"
              }`}
            >
              {copyState === "copied" ? <><Check size={10}/> Copied</>
                : copyState === "failed" ? <><AlertTriangle size={10}/> Copy failed</>
                : <><Copy size={10}/> Copy Payload JSON</>}
            </button>
            <button
              type="button"
              data-testid={`payload-builder-download${downloadState === "downloaded" ? "-success" : downloadState === "failed" ? "-failed" : ""}`}
              onClick={downloadJson}
              disabled={!generated}
              className={`px-3 py-1 rounded-sm border uppercase tracking-[0.12em] inline-flex items-center gap-1 ${
                downloadState === "downloaded"
                  ? "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]"
                  : downloadState === "failed"
                  ? "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]"
                  : "border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] disabled:opacity-40"
              }`}
            >
              {downloadState === "downloaded" ? <><Check size={10}/> Downloaded</>
                : downloadState === "failed" ? <><AlertTriangle size={10}/> Download failed</>
                : <><Download size={10}/> Download Payload JSON</>}
            </button>
            <button
              type="button"
              data-testid="payload-builder-clear"
              onClick={clearAll}
              className="px-3 py-1 rounded-sm border border-[#26262B] text-[#8A8A93] uppercase tracking-[0.12em] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
            >
              <Eraser size={10} /> Clear / Reset
            </button>
            <span className="ml-auto text-[#5F5F66] uppercase tracking-[0.12em]">
              Generated JSON is local only — paste it manually into the scanner panel.
            </span>
          </div>

          {/* ── Warnings ────────────────────────────────────────── */}
          {warnings.length > 0 && (
            <div
              data-testid="payload-builder-warnings"
              className="px-4 py-2 border-b border-[#26262B] text-[10px] font-mono text-[#FFB800] flex flex-col gap-1"
            >
              {warnings.map((w, i) => (
                <span
                  key={i}
                  data-testid={`payload-builder-warning-${i}`}
                  className="flex items-start gap-1"
                >
                  <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                  {w}
                </span>
              ))}
            </div>
          )}

          {/* ── Generated preview ───────────────────────────────── */}
          {generated && (
            <div className="px-4 py-3" data-testid="payload-builder-generated">
              <div className="flex items-center gap-2 mb-2 text-[10px] font-mono">
                <FileJson size={11} className="text-[#5F5F66]" />
                <span className="uppercase tracking-[0.14em] text-[#8A8A93]">
                  Generated payload (paste-ready)
                </span>
                <span className="ml-auto text-[#5F5F66]">
                  {Array.isArray(generated.watchlist) ? generated.watchlist.length : 0} symbol(s)
                </span>
              </div>
              <pre
                data-testid="payload-builder-generated-json"
                className="text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 rounded-sm border border-[#26262B] max-h-[28rem] overflow-auto"
              >
                {JSON.stringify(generated, null, 2)}
              </pre>
            </div>
          )}

          {!generated && (
            <div
              data-testid="payload-builder-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              Fill in fields and click Generate Payload. The generated
              JSON appears here for review, then paste into the
              {" "}{mod.label} scanner panel.
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ── Field building blocks ─────────────────────────────────────────────

function Field({ label, value, onChange, testid }) {
  return (
    <label className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
        {label}
      </span>
      <input
        type="text"
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[#E0E0E0] focus:outline-none focus:border-[#3F3F47]"
      />
    </label>
  );
}

function BoolField({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 min-w-0">
      <input
        type="checkbox"
        data-testid={testid}
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-[#22D3CC]"
      />
      <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
        {label}
      </span>
    </label>
  );
}

function TextArea({ label, testid, value, onChange, rows = 6 }) {
  return (
    <label className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[10px] font-mono text-[#5F5F66] uppercase tracking-[0.12em]">
        {label}
      </span>
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        rows={rows}
        className="bg-[#08080A] border border-[#26262B] rounded-sm p-2 text-[10px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#3F3F47]"
      />
    </label>
  );
}
